import React from 'react';
import { Box, Typography, CircularProgress, LinearProgress, Alert, Fade } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { colors } from '../styles/styleConstants';

/**
 * AIProcessingIndicator - Displays AI processing status for items
 * 
 * @param {string} status - Processing status: 'pending', 'processing', 'completed', 'failed'
 * @param {boolean} isGroupPhoto - Whether this item is from a group photo
 * @param {number} groupPhotoItemCount - Number of items in the group photo
 * @param {string} variant - Display variant: 'inline', 'overlay', 'banner'
 * @param {boolean} showCompleted - Whether to show completed state (auto-hides after delay)
 */
const AIProcessingIndicator = ({ 
  status = 'pending',
  isGroupPhoto = false,
  groupPhotoItemCount = 0,
  variant = 'inline',
  showCompleted = false,
  sx = {}
}) => {
  // Don't render if status is completed and we're not showing completed state
  if (status === 'completed' && !showCompleted) {
    return null;
  }

  // Don't render if no processing is happening
  if (!status || status === 'failed') {
    return null;
  }

  const isProcessing = status === 'pending' || status === 'processing';
  const isCompleted = status === 'completed';

  // Get appropriate message based on status and type
  const getMessage = () => {
    if (isGroupPhoto) {
      if (status === 'pending') {
        return `Queued: Processing ${groupPhotoItemCount} items from group photo...`;
      }
      if (status === 'processing') {
        return `AI is analyzing ${groupPhotoItemCount} items from group photo...`;
      }
      if (status === 'completed') {
        return `Successfully processed ${groupPhotoItemCount} items!`;
      }
    } else {
      if (status === 'pending') {
        return 'Queued for AI processing...';
      }
      if (status === 'processing') {
        return 'AI is generating description & tags...';
      }
      if (status === 'completed') {
        return 'AI processing complete!';
      }
    }
    return '';
  };

  // Inline variant - compact display for within content
  if (variant === 'inline') {
    return (
      <Fade in={true}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: isCompleted 
              ? 'rgba(76, 175, 80, 0.1)' 
              : 'rgba(33, 150, 243, 0.1)',
            border: `1px solid ${isCompleted ? 'rgba(76, 175, 80, 0.3)' : 'rgba(33, 150, 243, 0.3)'}`,
            ...sx
          }}
        >
          {isProcessing ? (
            <>
              {isGroupPhoto ? (
                <PhotoLibraryIcon sx={{ color: colors.actionBlue, fontSize: 20 }} />
              ) : (
                <AutoFixHighIcon sx={{ color: colors.actionBlue, fontSize: 20 }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: colors.darkSlateBlue, fontWeight: 500 }}>
                  {getMessage()}
                </Typography>
                <LinearProgress 
                  sx={{ 
                    mt: 0.5, 
                    height: 4, 
                    borderRadius: 2,
                    backgroundColor: 'rgba(33, 150, 243, 0.2)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: colors.actionBlue
                    }
                  }} 
                />
              </Box>
            </>
          ) : isCompleted ? (
            <>
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                {getMessage()}
              </Typography>
            </>
          ) : null}
        </Box>
      </Fade>
    );
  }

  // Overlay variant - for displaying over images
  if (variant === 'overlay') {
    return (
      <Fade in={true}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            borderRadius: 'inherit',
            zIndex: 10,
            ...sx
          }}
        >
          {isProcessing ? (
            <>
              <CircularProgress size={32} sx={{ color: 'white', mb: 1 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'white', 
                  textAlign: 'center',
                  px: 1,
                  fontSize: '0.75rem'
                }}
              >
                {status === 'pending' ? 'Queued...' : 'Processing...'}
              </Typography>
              {isGroupPhoto && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(255,255,255,0.7)', 
                    textAlign: 'center',
                    fontSize: '0.65rem',
                    mt: 0.5
                  }}
                >
                  {groupPhotoItemCount} items
                </Typography>
              )}
            </>
          ) : isCompleted ? (
            <>
              <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 32, mb: 0.5 }} />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#4caf50', 
                  textAlign: 'center',
                  fontSize: '0.75rem'
                }}
              >
                Done!
              </Typography>
            </>
          ) : null}
        </Box>
      </Fade>
    );
  }

  // Banner variant - full-width alert style
  if (variant === 'banner') {
    return (
      <Fade in={true}>
        <Alert
          severity={isCompleted ? 'success' : 'info'}
          icon={isGroupPhoto ? <PhotoLibraryIcon /> : <AutoFixHighIcon />}
          sx={{
            borderRadius: 2,
            mb: 2,
            '& .MuiAlert-message': {
              width: '100%'
            },
            ...sx
          }}
        >
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {getMessage()}
            </Typography>
            {isProcessing && (
              <LinearProgress 
                sx={{ 
                  mt: 1, 
                  height: 4, 
                  borderRadius: 2 
                }} 
              />
            )}
          </Box>
        </Alert>
      </Fade>
    );
  }

  return null;
};

export default AIProcessingIndicator;
